import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#F59E0B", "#EF4444", "#34D399", "#FDE68A", "#FFFFFF"];

const Confetti = () => {
  const bits = useMemo(() => Array.from({ length: 28 }, (_, i) => ({
    id: i,
    x: (Math.random() - 0.5) * 520,
    y: -80 - Math.random() * 260,
    r: Math.random() * 720 - 360,
    c: COLORS[i % COLORS.length],
    d: 0.9 + Math.random() * 0.8,
    w: 5 + Math.random() * 6,
  })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {bits.map((b) => (
        <motion.span
          key={b.id}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
          animate={{ x: b.x, y: [0, b.y, b.y + 320], opacity: [1, 1, 0], rotate: b.r, scale: [1, 1.1, 0.8] }}
          transition={{ duration: b.d + 0.8, ease: "easeOut", times: [0, 0.45, 1] }}
          className="absolute left-1/2 top-1/2 block"
          style={{ width: b.w, height: b.w * 0.55, background: b.c }}
        />
      ))}
    </div>
  );
};

export const WallCelebration = ({ show, onDone }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => { if (show) setOpen(true); }, [show]);

  const close = async () => {
    setOpen(false);
    try { await axios.post(`${API}/progress/wall-celebrated`); } catch (e) { console.warn("wall-celebrated POST failed:", e); }
    onDone?.();
  };

  const toWallPost = () => {
    close();
    setTimeout(() => document.querySelector('[data-testid="wall-post"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
          data-testid="wall-celebration"
          onClick={close}
        >
          <Confetti />
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.6, ease: [0.65, 0, 0.35, 1] }}
            className="relative w-full max-w-md border border-[#F59E0B]/50 bg-[#FFFFFF] p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={close} aria-label="Close" data-testid="wall-celebration-close" className="absolute top-3 right-3 p-2 text-[#78716C] hover:text-[#1C1917] transition-colors">
              <X size={16} />
            </button>
            <div className="section-tag mb-4 flex items-center gap-2"><Users size={13} /> § The Graduate Wall</div>
            <h3 className="font-display text-4xl leading-[0.98] tracking-tight mb-4">
              You're on <span className="italic text-emerald-300">the wall</span>.
            </h3>
            <p className="text-[#78716C] text-sm leading-relaxed mb-6">
              Your card just went up on the public Graduate Wall at tacc.love — proof that someone went through all eight.
              It's anonymous unless you choose otherwise.
            </p>
            <div className="h-px w-full bg-gradient-to-r from-red-500/80 via-[#F59E0B]/80 to-emerald-400/80 mb-6" />
            <div className="flex flex-wrap gap-3">
              <button onClick={toWallPost} className="btn-ember text-xs" data-testid="wall-celebration-choose">Choose how it reads</button>
              <Link to="/#graduate-wall" onClick={close} className="btn-outline text-xs" data-testid="wall-celebration-see">See the wall</Link>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
